import { client, initializeClient } from '@/services/socket-io-client';
import errorHandlers from '@/services/error-handlers';
import events from '@/services/events';
import http from '@/services/http';



export default {
  namespaced: true,

  state: {
    initialized: false,
    orderRoom: {
      attemptedToJoin: false,
      changingToSplitType: null,
      disableSplit: false,
      errors: [],
      id: null,
      joining: false,
      lostConnection: false,
      orderIsSplit: false,
      otherUsers: [],
      splitType: 'even',
      status: null,
      you: {
        id: null,
        index: 0,
        color: null,
        disconnected: false,
        items: [],
        lockedIn: false,
        nickname: null,
        paid: false,
        receiptToken: null,
        serviceCharges: 0,
        tax: 0,
        total: 0
      },
      youClosedOrder: false
    }
  },

  getters: {
    isPrimaryOrderRoomUser(state) {
      return !!state.orderRoom.you.id && state.orderRoom.you.index === 0;
    },

    orderRoom(state) {
      return state.orderRoom;
    },

    orderRoomErrors(state, getters, rootState, rootGetters) {
      let hasErrors = false;

      const retryCaptureErrorCodes = [
        'complete-payment-failed-pos-unavailable',
        'order-fulfill-failed-without-payment-error',
        'order-fulfill-partial-payment-error',
        'pos-degraded',
        'pos-generic-error',
        'store-unavailable',
        'ticket-locked-at-pos'
      ];

      const orderErrors = rootGetters['orders/orderErrors'];
      const otherErrors = [...orderErrors];
      const retryCaptureErrors = [];

      if (state.orderRoom.status === 'error') {
        state.orderRoom.errors.forEach((error) => {
          if (retryCaptureErrorCodes.includes(error.code)) {
            retryCaptureErrors.push(error);
          }

          else {
            otherErrors.push(error);
          }
        });
      }

      hasErrors = otherErrors.length + retryCaptureErrors.length > 0;

      return {
        hasErrors,
        otherErrors,
        retryCaptureErrors
      };
    }
  },

  actions: {
    async allUsersHavePaid({ dispatch }, event) {
      try {
        if (!event.orderWasClosed) {
          await dispatch('retryPaymentCapture');
        }
      }

      catch (error) {
        errorHandlers.silent(error);
      }
    },

    backendSocketError() {
      const error = new Error('backend socket error');

      errorHandlers.generic(error, {
        fallbackMessage: 'Your socket connection failed. Please reload your browser and try again.',
        important: true
      });
    },

    cancelPayments(context, options) {
      client.emit('cancel-payments', { reason: options.reason });
    },

    changeSplitType({ commit }, options) {
      commit('SET_SPLIT_TYPE_IS_CHANGING', options);

      client.emit('change-split-type', {
        splitType: options.splitType
      });
    },

    changeCustomPaymentAmount(context, options) {
      client.emit('change-custom-payment-amount', {
        total: options.total
      });
    },

    connect({ dispatch, state }) {
      if (state.orderRoom.lostConnection && state.orderRoom.attemptedToJoin) {
        dispatch('joinOrderRoom');
      }
    },

    denied(context, event) {
      if (event.reason === 'already-paid') {
        events.emit('full-page-message', {
          title: 'Oops!',
          subtitle: 'Payment is pending for this order.',
          description: 'If you need to pay, ask someone in your group to cancel payment and try again.',
          button: 'goToDefaultPage'
        });
      }

      else if (event.reason === 'max-users') {
        if (event.maxUsers === 1) {
          events.emit('full-page-message', {
            title: 'Oops!',
            subtitle: 'Guests aren\'t allowed to split the bill.',
            description: 'If you need to pay, ask someone in your group to disconnect and try again.',
            button: 'goToDefaultPage'
          });
        }

        else {
          events.emit('full-page-message', {
            title: 'Oops!',
            subtitle: `Only ${event.maxUsers} guests are allowed to split the bill.`,
            description: 'If you need to pay, ask someone in your group to disconnect and try again.',
            button: 'goToDefaultPage'
          });
        }
      }

      client.close();
    },

    disconnect({ commit }, reason) {
      commit('SET_DISCONNECT');

      if (reason === 'io server disconnect') {
        /*
          NOTE: This means the server has forcefully disconnected the socket with socket.disconnect()
          This only occurs during the drain deployment process (POST /service/drain). What we want
          to do is reconnect after a slight delay, which should reconnect us to a different node
          that isn't currently draining. For any other disconnect reason, Socket IO will automatically
          take care of reconnecting.
        */

        setTimeout(() => {
          client.connect();
        }, 100);
      }
    },

    initialize({ commit, dispatch, state }) {
      if (state.initialized) {
        return;
      }

      initializeClient();

      client.on('all-users-have-paid', dispatch.bind(this, 'allUsersHavePaid'));
      client.on('backend-socket-error', dispatch.bind(this, 'backendSocketError'));
      client.on('connect', dispatch.bind(this, 'connect'));
      client.on('denied', dispatch.bind(this, 'denied'));
      client.on('disconnect', dispatch.bind(this, 'disconnect'));
      client.on('order-synced', dispatch.bind(this, 'orderSynced'));
      client.on('payments-cancelled', dispatch.bind(this, 'paymentsCancelled'));
      client.on('room-state-updated', dispatch.bind(this, 'roomStateUpdated'));
      client.on('split-type-change-finished', dispatch.bind(this, 'splitTypeChangeFinished'));
      client.on('user-connected', dispatch.bind(this, 'userConnected'));
      client.on('user-disconnected', dispatch.bind(this, 'userDisconnected'));
      client.on('user-paid', dispatch.bind(this, 'userPaid'));
      client.on('user-reconnected', dispatch.bind(this, 'userReconnected'));

      commit('SET_INITIALIZED', true);
    },

    joinOrderRoom({ commit, rootGetters }) {
      const merchant = rootGetters['merchant/merchant'];
      const order = rootGetters['orders/order'];
      const { route } = rootGetters['session/session'];
      const user = rootGetters['session/websocketUser'];
      const isExcludedUser = rootGetters['session/isTableTopDeviceOrdering'];

      commit('SET_ORDER_ROOM_JOINED');
      // If tabletop device exclude the table top device a "user" from the room so when a guest or guests checkout
      // the table top device is not included in the user list. So, isExcludedUser will be true.
      client.emit('join-room', {
        merchant,
        order,
        route,
        user,
        isExcludedUser
      });
    },

    joinOrderRoomConditionally({ dispatch, rootGetters, state }) {
      const order = rootGetters['orders/order'];

      if (!state.orderRoom.attemptedToJoin && order.orderId != null && order.orderState !== 'Closed') {
        dispatch('joinOrderRoom');
      }
    },

    leaveOrderRoom({ commit, dispatch }) {
      client.emit('leave-room');

      dispatch('orders/syncOrdersAtTableIntervalStop', null, {
        root: true
      });

      dispatch('session/unsetOrder', {}, {
        root: true
      });

      commit('UNSET_ORDER_ROOM');
    },

    async orderClosed({ dispatch, state, rootGetters }) {
      const isExcludedUser = rootGetters['session/isTableTopDeviceOrdering'];

      if (!isExcludedUser) {
        dispatch('receipt/reloadPatReceipt', null, { root: true });

        if (!state.orderRoom.you.paid) {
          dispatch('orderClosedWithoutYou');
        }
      }
    },

    orderClosedWithoutYou({ rootGetters }) {
      const order = rootGetters['orders/order'];

      events.emit('full-page-message', {
        title: 'Order Closed',
        subtitle: `Your group closed the bill without you. Check number: ${order.ticketNumber}`,
        description: 'You\'re free to leave. Thanks for dining with us!',
        button: 'goToDefaultPage'
      });

      client.close();
    },

    orderLost({ rootGetters }) {
      const order = rootGetters['orders/order'];

      events.emit('full-page-message', {
        title: 'Order Closed',
        subtitle: `Looks like your check has been paid. Check number: ${order.ticketNumber}`,
        description: 'Please confirm with your waitstaff, and you\'re free to go!',
        button: 'goToDefaultPage'
      });

      client.close();
    },

    orderSynced({ commit, rootState, state }, event) {
      if (state.orderRoom.you.paid) {
        /*
          NOTE: Once a user has paid, there are a few things we don't want to be updated:
          - payments (due to the last paying user seeing all payments)
          - orderItems (due to itemized split)
        */

        event.order.payments = rootState.orders.order.payments;
        event.order.orderItems = rootState.orders.order.orderItems;
      }

      commit('orders/SET_ORDER', event.order, {
        root: true
      });
    },

    payment({ commit }, options) {
      commit('SET_YOU_CLOSED_ORDER', !options.retryCapture && options.orderWasClosed);

      client.emit('payment', {
        orderWasClosed: options.orderWasClosed,
        receiptToken: options.payment ? options.payment.receiptToken : null
      });
    },

    paymentsCancelled({ state, rootGetters }, event = {}) {
      const isTableTopDeviceOrdering = rootGetters['session/isTableTopDeviceOrdering'];

      const buttonType = isTableTopDeviceOrdering
        ? 'goToTableTopViewBill'
        : 'goToOrderPage';

      if (event.reason === 'order-changed-after-payment') {
        events.emit('full-page-message', {
          title: 'Payment Cancelled',
          subtitle: 'It appears that your order changed. All payments were cancelled.',
          description: 'To pay your bill, click "View Check" or scan the QR code again.',
          button: buttonType
        });

        client.close();
      }

      else if (event.reason === 'order-changed-upon-payment') {
        events.emit('notification', {
          type: 'error',
          message: 'Your order changed. Please review before paying.'
        });
      }

      else if (event.reason === 'manual') {
        let subtitle = `${event.user.nickname} cancelled your group's payments.`;
        const youCancelled = event.user.id === state.orderRoom.you.id;

        if (youCancelled) {
          subtitle = 'You\'ve cancelled your group\'s payments.';
        }

        events.emit('full-page-message', {
          title: 'Payment Cancelled',
          subtitle,
          description: 'To pay your bill, click "View Check" or scan the QR code again.',
          button: buttonType
        });

        client.close();
      }

      else {
        events.emit('full-page-message', {
          type: 'cancelled-payment',
          button: 'goToDefaultPage'
        });

        client.close();
      }
    },

    paymentCaptureFailure(context, options) {
      client.emit('payment-capture-failure', {
        capiErrorResponse: options.capiErrorResponse,
        receiptToken: options.payment ? options.payment.receiptToken : null
      });
    },

    profileUpdate({ commit, dispatch }, options) {
      client.emit('profile-update', {
        nickname: options.nickname
      });

      dispatch('session/updateWebsocketUser', options, {
        root: true
      });

      commit('SET_PROFILE', options);
    },

    reconnectedToOrderRoom({ dispatch }) {
      dispatch('receipt/reloadPatReceipt', null, {
        root: true
      });
    },

    async respondToUpdatedRoomStatus({ dispatch, state }) {
      if (state.orderRoom.status === 'lost') {
        dispatch('orderLost');
      }

      else if (state.orderRoom.status === 'closed') {
        dispatch('orderClosed');
      }
    },

    async retryPaymentCapture({ dispatch, rootGetters }) {
      const order = rootGetters['orders/order'];

      try {
        await http.post(`/cardfree-api/v1/account/me/order/${order.orderId}/payment/complete`);

        dispatch('payment', {
          orderWasClosed: true,
          retryCapture: true
        });
      }

      catch (error) {
        if (error.response) {
          errorHandlers.silent(error);

          dispatch('paymentCaptureFailure', {
            capiErrorResponse: error.response
          });
        }

        else {
          errorHandlers.generic(error);
        }

        throw error;
      }
    },

    roomStateUpdated({ commit, dispatch, rootGetters, state }, room) {
      const websocketUser = rootGetters['session/websocketUser'];

      const hadLostConnection = state.orderRoom.lostConnection;

      commit('SET_ORDER_ROOM', { websocketUser, room });

      if (state.orderRoom.you.disconnected) {
        /*
          NOTE: This edge case happens if the user has
          two tabs pulled up for the same order, and
          then closes one. The tab that remains open
          will need to rejoin the room:
        */

        dispatch('joinOrderRoom');
      }

      if (hadLostConnection) {
        dispatch('reconnectedToOrderRoom');
      }

      dispatch('respondToUpdatedRoomStatus');

      dispatch('orders/assignOrderToUser', null, {
        root: true
      });
    },

    selectItems(context, items) {
      client.emit('select-items', {
        items
      });
    },

    splitTypeChangeFinished({ commit }, result) {
      if (result.changeWasRejected) {
        events.emit('notification', {
          type: 'error',
          message: 'Can\'t change split after a guest has paid.'
        });
      }

      commit('SET_SPLIT_TYPE_CHANGE_FINISHED');
    },

    syncOrdersAtTable({ rootGetters }, event) {
      const merchant = rootGetters['merchant/merchant'];

      event.merchantId = merchant.id;

      client.emit('sync-orders', event);
    },

    userConnected(context, user) {
      events.emit('notification', {
        type: 'success',
        subject: user.nickname,
        message: 'is now splitting your bill.'
      });
    },

    userDisconnected(context, user) {
      events.emit('notification', {
        type: 'error',
        subject: user.nickname,
        message: 'has disconnected.'
      });
    },

    userPaid(context, user) {
      events.emit('notification', {
        type: 'success',
        subject: user.nickname,
        message: 'has authorized payment.'
      });
    },

    userReconnected(context, user) {
      events.emit('notification', {
        type: 'success',
        subject: user.nickname,
        message: 'has reconnected.'
      });
    },

    updateOrderRoomWithReceipt(context, receipt) {
      context.commit('SET_ORDER_ROOM_WITH_RECEIPT', receipt);
    }
  },

  mutations: {
    SET_DISCONNECT(state) {
      state.orderRoom.lostConnection = true;
    },

    SET_INITIALIZED(state, initialized) {
      state.initialized = initialized;
    },

    SET_ORDER_ROOM(state, { websocketUser, room }) {
      const yourUserId = websocketUser.id;

      const otherUsers = room.users.filter((u, i) => {
        u.index = i;

        return (!u.disconnected || u.lockedIn) && u.id !== yourUserId;
      });

      const you = room.users.find(u => u.id === yourUserId);

      state.orderRoom.disableSplit = room.disableSplit;
      state.orderRoom.errors = room.errors;
      state.orderRoom.id = room.id;
      state.orderRoom.orderIsSplit = otherUsers.length > 0;
      state.orderRoom.otherUsers = otherUsers;
      state.orderRoom.splitType = room.splitType;
      state.orderRoom.status = room.status;

      if (you) {
        /*
          NOTE: We make sure "you" is defined before updating your user
          state. There's a very rare race condition that can happen while in the
          process of joining a room. If you're currently joining, you
          might receive an "early" room state update due to current guests
          interacting in the room before your state is saved and returned.
          In this case, we do nothing and wait for "you" to come through
          on the next update.
        */

        state.orderRoom.lostConnection = false;
        state.orderRoom.joining = false;
        state.orderRoom.you = you;
      }
    },

    SET_ORDER_ROOM_JOINED(state) {
      state.orderRoom.attemptedToJoin = true;
      state.orderRoom.joining = true;
    },

    SET_ORDER_ROOM_WITH_RECEIPT(state, receipt) {
      const paymentTotalCents = Math.round(receipt.yourPayment.total * 100);
      const orderTotalCents = Math.round(receipt.order.total * 100) - Math.round(receipt.order.externalPaymentAmount * 100);

      state.orderRoom.orderIsSplit = paymentTotalCents < orderTotalCents;
      state.orderRoom.splitType = receipt.order.isPaymentItemized ? 'itemized' : 'even';
    },

    SET_PROFILE(state, options) {
      state.orderRoom.you.nickname = options.nickname;
    },

    SET_SPLIT_TYPE_CHANGE_FINISHED(state) {
      state.orderRoom.changingToSplitType = null;
    },

    SET_SPLIT_TYPE_IS_CHANGING(state, type) {
      state.orderRoom.changingToSplitType = type;
    },

    SET_YOU_CLOSED_ORDER(state, didClose) {
      state.orderRoom.youClosedOrder = didClose;
    },

    UNSET_ORDER_ROOM(state) {
      Object.assign(state.orderRoom, {
        attemptedToJoin: false,
        changingToSplitType: null,
        disableSplit: false,
        errors: [],
        id: null,
        joining: false,
        lostConnection: false,
        orderIsSplit: false,
        otherUsers: [],
        splitType: 'even',
        status: null,
        you: {
          id: null,
          index: 0,
          color: null,
          disconnected: false,
          items: [],
          lockedIn: false,
          nickname: null,
          paid: false,
          receiptToken: null,
          serviceCharges: 0,
          tax: 0,
          total: 0
        },
        youClosedOrder: false
      });
    }
  }
};
